/**
 * PromoteAffiliateDialog — ★ Fase 59: promozione Affiliato → Agente CRM.
 *
 * L'admin sceglie il ruolo target (Associato o Senior). Il backend crea/riattiva
 * la scheda agente, aggiorna l'utente (stesse credenziali) e AZZERA il training:
 * il promosso dovrà completare il percorso del nuovo ruolo (blocco attivo).
 */
import React, { useEffect, useState } from 'react';
import { api, getCrmTenant } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, TrendingUp, GraduationCap } from 'lucide-react';

export function PromoteAffiliateDialog({ affiliate, open, onClose, onChanged }) {
  // ★ 2026-09-04 (Fase 28) — CRM IT: il ruolo "Associato" non esiste più →
  // default e unica opzione operativa: Senior.
  const isItTenant = getCrmTenant() === 'it';
  const defaultRole = isItTenant ? 'senior_associate' : 'associate_manager';
  const [targetRole, setTargetRole] = useState(defaultRole);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setTargetRole(defaultRole);
  }, [open, affiliate?.id, defaultRole]);

  if (!affiliate) return null;

  const confirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/affiliates/${affiliate.id}/promote`, {
        target_role: targetRole,
      });
      const label = targetRole === 'senior_associate' ? 'Senior' : 'Associato';
      toast.success(`${affiliate.name} promosso a ${label}!`, {
        description: `Training azzerato: dovrà completare il percorso ${data.track_role === 'senior' ? 'Senior' : 'Associato'} nel CRM (stesse credenziali di accesso).`,
      });
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore promozione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="promote-affiliate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[hsl(var(--primary))]" /> Promuovi · {affiliate.name}
          </DialogTitle>
          <DialogDescription>
            L&apos;affiliato diventa un agente CRM a tutti gli effetti: il profilo affiliato viene
            disattivato (il link referral resta storicizzato) e accederà al CRM con le stesse
            credenziali.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <Select value={targetRole} onValueChange={setTargetRole}>
            <SelectTrigger data-testid="promote-affiliate-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {!isItTenant && (
                <SelectItem value="associate_manager" data-testid="promote-role-associate">
                  <span className="font-medium">Associato</span>
                  <span className="text-muted-foreground text-xs"> — qualifica i lead e li passa al Senior</span>
                </SelectItem>
              )}
              <SelectItem value="senior_associate" data-testid="promote-role-senior">
                <span className="font-medium">Senior</span>
                <span className="text-muted-foreground text-xs"> — show, proposte e chiusura</span>
              </SelectItem>
            </SelectContent>
          </Select>

          <div
            className="flex items-start gap-2 rounded-md border border-[#E4C88A] bg-[#FBF3DF] p-3 text-xs text-[#6A4B00]"
            data-testid="promote-affiliate-training-warning"
          >
            <GraduationCap className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <b>Training azzerato.</b> Al primo accesso dovrà completare il percorso del nuovo
              ruolo: il CRM resta bloccato finché non ottiene il Certificato HCF.
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="promote-affiliate-cancel">
            Annulla
          </Button>
          <Button
            onClick={confirm}
            disabled={saving}
            data-testid="promote-affiliate-confirm"
            className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Promuovi ad agente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
